import { Button, Input, LoadingClaro } from 'common-ui-components';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { selectConfig, selectPromoter } from '../../redux/selectors';
import { generateIdu, setIdu, setPromoterData } from '../../redux/Slice/promoterSlice';
import { navigateTo } from '../../redux/Slice/navigationSlice';
import { DOCUMENT_TYPES_ARGENTINA, EMAIL_REGEX, PHONE_REGEX } from '../../utils/constants';
import { NumericInput } from '../../components/NumericInput';
import { createPhoneChangeHandler } from '../../utils/utils';
import { useMemo } from 'react';
import Terms from '../../components/Terms';

const WelcomeForm = () => {
  const {
    control,
    handleSubmit,
    formState: { isValid }
  } = useForm({ mode: 'onChange', shouldFocusError: false, defaultValues: { documentType: 'DNI', documentNumber: '', phoneNumber: '', email: '', terms: false } });
  const dispatch = useDispatch();
  const {
    language: { welcomeForm: text, termsReferralsProgram: termsText },
  } = useSelector(selectConfig);
  const { loading } = useSelector(selectPromoter);
  const selectedTypeId = useWatch({ control, name: 'documentType' });
  const selectedType = useMemo(
    () => DOCUMENT_TYPES_ARGENTINA.find((doc) => doc.id === selectedTypeId),
    [selectedTypeId],
  );
  const maxLength = selectedType?.maxLength ?? undefined;
  const minLength = selectedType?.minLength ?? undefined;

  const onSubmit = async (data) => {
    try {
      const response = await dispatch(generateIdu(data)).unwrap();
      if (!response?.idu) {
        throw new Error('IDU inexistente');
      }
      dispatch(setPromoterData(data));
      dispatch(setIdu(response.idu));
      dispatch(navigateTo('EndProcess'));
      return;
    } catch (error) {
      switch (error?.errorCode) {
        case '100001':
          dispatch(navigateTo('NoActiveServiceError'));
          break;
        case '100002':
          dispatch(navigateTo('NotMeetingRequirementsError'));
          break;
        case '100003':
          dispatch(navigateTo('VerifyDataError'));
          break;
        case '100004':
        case '100005':
          dispatch(navigateTo('CantRefferNewClientsError'));
          break;
        default:
          dispatch(navigateTo('CantRefferNewClientsError'));
      }
    }
  };

  return (
    <section className='section welcomeForm-container'>
      {loading && (
        <LoadingClaro
          text={'Validando datos... Esperá por favor.'}
          fullViewportSize={true}
          overlay={true}
        />
      )}
      <h2 className='welcomeForm-title'>{text.title}</h2>
      <form className='welcomeForm-form' onSubmit={handleSubmit(onSubmit)}>
        <div className='welcomeForm-inputWrapper'>
          <Controller
            name='documentNumber'
            defaultValue=''
            control={control}
            rules={{
              required: { value: true, message: 'Obligatorio' },
              validate: (value) => {
                if (!selectedType) return true;

                if (minLength && value.length < minLength) {
                  return `Mínimo ${minLength} caracteres`;
                }
                if (maxLength && value.length > maxLength) {
                  return `Máximo ${maxLength} caracteres`;
                }
                return true;
              },
            }}
            render={({
              field: { name, onChange, ref, value, disabled },
              fieldState: { error },
            }) => (
              <NumericInput
                id='documentNumber'
                data-testid='documentNumber'
                name={name}
                onChange={onChange}
                ref={ref}
                value={value}
                disabled={disabled}
                status={error ? 'error' : 'default'}
                label='Número de documento'
                labelText='Número de documento'
                helpText={error ? error?.message : ''}
                maxLength={maxLength}
              />
            )}
          />
        </div>
        <div className='welcomeForm-inputWrapper'>
          <Controller
            name='email'
            defaultValue=''
            control={control}
            rules={{
              required: { value: true, message: 'Obligatorio' },
              pattern: { value: EMAIL_REGEX, message: 'Formato incorrecto' },
              maxLength: 60,
            }}
            render={({
              field: { name, onChange, ref, value, disabled },
              fieldState: { error },
            }) => (
              <Input
                id='email'
                data-testid='email'
                name={name}
                onChange={onChange}
                ref={ref}
                value={value}
                disabled={disabled}
                status={error ? 'error' : 'default'}
                label='E-mail'
                labelText='E-mail'
                helpText={error ? error?.message : ''}
                typeInput='email'
                maxLength={60}
              />
            )}
          />
        </div>
        <div className='welcomeForm-inputWrapper-large'>
          <Controller
            name='phoneNumber'
            defaultValue=''
            control={control}
            rules={{
              required: { value: true, message: 'Ingresá tu número de línea sin 0 y sin 15' },
              pattern: { value: PHONE_REGEX, message: 'Ingresá tu número de línea sin 0 y sin 15' },
            }}
            render={({
              field: { name, onChange, ref, value, disabled },
              fieldState: { error },
            }) => (
              <NumericInput
                id='phoneNumber'
                data-testid='phoneNumber'
                name={name}
                onChange={createPhoneChangeHandler(onChange, value)}
                ref={ref}
                value={value}
                disabled={disabled}
                status={error ? 'error' : 'default'}
                label='Número de teléfono'
                labelText='Número de teléfono'
                helpText={error ? error.message : <span className='welcomeForm-helpText' dangerouslySetInnerHTML={{ __html: text.phoneNumberHelpText }} />}
                maxLength={13}
              />
            )}
          />
        </div>
        <Controller
          name='terms'
          defaultValue={false}
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { name, onChange, value } }) => (
            <Terms onChange={onChange} value={value} name={name} text={termsText} />
          )}
        />
        <Button
          className='submit-button-error'
          type='submit'
          data-testid='welcomeForm-continue-button'
          disabled={!isValid || loading}
        >
          {text.button}
        </Button>
      </form>
    </section>
  );
};

export default WelcomeForm;
